import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  createCustomWorkflow,
  getWorkflowApproverRoles,
  getWorkflows,
  updateWorkflow,
} from "../../api/workflows";
import { getEmployees } from "../../api/employees";
import { roleDisplayName } from "../../utils/requestTransparency";
import {
  WORKFLOW_FAMILY_LABELS,
  getWorkflowAudienceLabel,
  getWorkflowDisplayName,
  getWorkflowFamilyKey,
  getWorkflowRuntimeNote,
  groupWorkflowsByFamily,
} from "../../utils/workflowCycleLabels";
import "./HrWorkflows.css";

function getApiErrorMessage(err, fallback) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return err?.message || fallback;
}

function stepPendingCount(workflow, stepOrder) {
  const pending = workflow?.pendingByStep || {};
  return Number(pending[stepOrder] || pending[String(stepOrder)] || 0);
}

function cloneWorkflowForEdit(workflow) {
  return {
    name: getWorkflowDisplayName(workflow),
    description: workflow.description || "",
    isActive: workflow.isActive !== false,
    family: getWorkflowFamilyKey(workflow.requestType),
    employeeIds: (workflow.assignees || [])
      .map((row) => row.employeeId || row.employee?.id)
      .filter(Boolean),
    steps: (workflow.steps || []).map((step) => ({
      stepOrder: step.stepOrder,
      approverRoleId: step.approverRoleId || step.approverRole?.id || "",
      isRequired: step.isRequired !== false,
      description: step.description || "",
    })),
  };
}

function employeeLabel(employee) {
  const name = employee?.fullName || employee?.name || "موظف";
  const code = employee?.employeeCode || employee?.code || "";
  return code ? `${name} (${code})` : name;
}

function HrWorkflows() {
  const [workflows, setWorkflows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [familyFilter, setFamilyFilter] = useState("all");
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");
  const [employees, setEmployees] = useState([]);
  const [employeeQuery, setEmployeeQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [workflowRows, roleRows, employeeResult] = await Promise.all([
        getWorkflows(),
        getWorkflowApproverRoles(),
        getEmployees().catch(() => []),
      ]);
      setWorkflows(Array.isArray(workflowRows) ? workflowRows : []);
      setRoles(Array.isArray(roleRows) ? roleRows : []);
      const list = Array.isArray(employeeResult)
        ? employeeResult
        : employeeResult?.data || [];
      setEmployees(list.filter((row) => row?.id));
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحميل سايكل الموافقات"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const families = useMemo(
    () => groupWorkflowsByFamily(workflows),
    [workflows],
  );

  const visibleFamilies = useMemo(() => {
    const needle = query.trim();
    return families
      .filter((family) => familyFilter === "all" || family.key === familyFilter)
      .map((family) => ({
        ...family,
        items: family.items.filter((workflow) => {
          if (!needle) return true;
          const haystack = [
            getWorkflowDisplayName(workflow),
            workflow.name,
            workflow.requestType,
            workflow.description,
            getWorkflowAudienceLabel(workflow.requestType, workflow),
            ...(workflow.assignees || []).map(
              (row) => row.employee?.fullName || "",
            ),
            ...(workflow.steps || []).map(
              (step) =>
                roleDisplayName(step.approverRole?.slug, step.approverRole?.name),
            ),
          ]
            .join(" ")
            .toLowerCase();
          return haystack.includes(needle.toLowerCase());
        }),
      }))
      .filter((family) => family.items.length);
  }, [families, familyFilter, query]);

  const displayFamilies = useMemo(() => {
    if (editingId !== "new" || !draft) return visibleFamilies;
    return [
      {
        key: "_new",
        label: "سايكل جديدة",
        items: [
          {
            id: "new",
            isCustom: true,
            requestType: `${draft.family || "appeal"}_custom_new`,
            name: draft.name || "سايكل جديدة",
            description: draft.description,
            steps: [],
            assignees: [],
            isActive: true,
          },
        ],
      },
      ...visibleFamilies,
    ];
  }, [visibleFamilies, editingId, draft]);

  const openTotal = useMemo(
    () =>
      workflows.reduce(
        (sum, workflow) => sum + Number(workflow.openRequestCount || 0),
        0,
      ),
    [workflows],
  );

  function startEdit(workflow) {
    setEditingId(workflow.id);
    setDraft(cloneWorkflowForEdit(workflow));
    setEmployeeQuery("");
    setSaveError("");
    setSaveSuccess("");
  }

  function startCreate() {
    setEditingId("new");
    setEmployeeQuery("");
    setDraft({
      name: "",
      description: "",
      isActive: true,
      family: "appeal",
      employeeIds: [],
      steps: [
        {
          stepOrder: 1,
          approverRoleId: roles[0]?.id || "",
          isRequired: true,
          description: "",
        },
      ],
    });
    setSaveError("");
    setSaveSuccess("");
  }

  function toggleEmployee(employeeId) {
    setDraft((current) => {
      if (!current) return current;
      const selected = current.employeeIds || [];
      return {
        ...current,
        employeeIds: selected.includes(employeeId)
          ? selected.filter((id) => id !== employeeId)
          : [...selected, employeeId],
      };
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setDraft(null);
    setSaveError("");
  }

  function updateDraft(patch) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  function updateStep(index, patch) {
    setDraft((current) => {
      const steps = current.steps.map((step, stepIndex) =>
        stepIndex === index ? { ...step, ...patch } : step,
      );
      return { ...current, steps };
    });
  }

  function addStep() {
    setDraft((current) => ({
      ...current,
      steps: [
        ...current.steps,
        {
          stepOrder: current.steps.length + 1,
          approverRoleId: roles[0]?.id || "",
          isRequired: true,
          description: "",
        },
      ],
    }));
  }

  function removeStep(index) {
    setDraft((current) => ({
      ...current,
      steps: current.steps
        .filter((_, stepIndex) => stepIndex !== index)
        .map((step, stepIndex) => ({ ...step, stepOrder: stepIndex + 1 })),
    }));
  }

  function moveStep(index, direction) {
    setDraft((current) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= current.steps.length) return current;
      const steps = [...current.steps];
      const [moved] = steps.splice(index, 1);
      steps.splice(nextIndex, 0, moved);
      return {
        ...current,
        steps: steps.map((step, stepIndex) => ({
          ...step,
          stepOrder: stepIndex + 1,
        })),
      };
    });
  }

  async function handleSave(workflow) {
    if (!draft) return;
    setSaveError("");
    setSaveSuccess("");

    if (!draft.name.trim()) {
      setSaveError("من فضلك أدخل اسم السايكل");
      return;
    }
    if (!draft.steps.length) {
      setSaveError("السايكل لازم يبقى فيها خطوة واحدة على الأقل");
      return;
    }
    if (draft.steps.some((step) => !step.approverRoleId)) {
      setSaveError("اختار دور المعتمد لكل خطوة");
      return;
    }

    const isNew = !workflow || workflow.id === "new" || editingId === "new";
    if (isNew && !draft.family) {
      setSaveError("اختار نوع السايكل");
      return;
    }
    if ((isNew || workflow?.isCustom) && !(draft.employeeIds || []).length) {
      setSaveError("اختار موظف واحد على الأقل");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim(),
        description: draft.description.trim() || null,
        isActive: draft.isActive,
        steps: draft.steps.map((step, index) => ({
          stepOrder: index + 1,
          approverRoleId: step.approverRoleId,
          isRequired: step.isRequired !== false,
          description: step.description.trim() || undefined,
        })),
      };
      const saved = isNew
        ? await createCustomWorkflow({
            name: payload.name,
            description: payload.description,
            family: draft.family,
            employeeIds: draft.employeeIds,
            steps: payload.steps,
          })
        : await updateWorkflow(workflow.id, {
            ...payload,
            ...(workflow.isCustom ? { employeeIds: draft.employeeIds } : {}),
          });
      setWorkflows((current) =>
        current.map((item) =>
          item.id === workflow.id
            ? {
                ...item,
                ...saved,
                pendingByStep: item.pendingByStep,
                openRequestCount: item.openRequestCount,
              }
            : item,
        ),
      );
      setSaveSuccess("تم حفظ السايكل");
      setEditingId(null);
      setDraft(null);
      await load();
    } catch (err) {
      setSaveError(getApiErrorMessage(err, "تعذر حفظ السايكل"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="hr-workflows" dir="rtl">
      <div className="hr-workflows-header">
        <div>
          <h1 className="hr-workflows-title">سايكل الموافقات</h1>
          <p className="hr-workflows-subtitle">
            شوف مسار كل سايكل، الطلبات الواقفة عند كل خطوة، وعدّل الأدوار من هنا.
          </p>
        </div>
        <div className="hr-workflows-stats">
          <span>{workflows.length} سايكل</span>
          <span>{openTotal} طلب مفتوح</span>
          <button type="button" className="hr-workflows-create-btn" onClick={startCreate}>
            سايكل جديدة
          </button>
        </div>
      </div>

      <div className="hr-workflows-toolbar">
        <input
          className="hr-workflows-search"
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="ابحث باسم السايكل أو الدور"
        />
        <div className="hr-workflows-filters">
          <button
            type="button"
            className={`hr-workflows-chip${familyFilter === "all" ? " is-active" : ""}`}
            onClick={() => setFamilyFilter("all")}
          >
            الكل
          </button>
          {families.map((family) => (
            <button
              key={family.key}
              type="button"
              className={`hr-workflows-chip${familyFilter === family.key ? " is-active" : ""}`}
              onClick={() => setFamilyFilter(family.key)}
            >
              {family.label}
            </button>
          ))}
        </div>
      </div>

      {error ? <p className="hr-workflows-alert error">{error}</p> : null}
      {saveSuccess ? (
        <p className="hr-workflows-alert success">{saveSuccess}</p>
      ) : null}
      {loading ? <p className="hr-workflows-empty">جاري تحميل السايكلز...</p> : null}
      {!loading && !error && !displayFamilies.length ? (
        <p className="hr-workflows-empty">مفيش سايكل مطابقة للبحث.</p>
      ) : null}

      {displayFamilies.map((family) => (
        <section key={family.key} className="hr-workflows-section">
          <h2 className="hr-workflows-section-title">{family.label}</h2>
          <div className="hr-workflows-grid">
            {family.items.map((workflow) => {
              const editing = editingId === workflow.id;
              const note = getWorkflowRuntimeNote(workflow.requestType);
              return (
                <article key={workflow.id} className="hr-workflows-card">
                  <div className="hr-workflows-card-top">
                    <div>
                      <h3 className="hr-workflows-card-name">
                        {getWorkflowDisplayName(workflow)}
                      </h3>
                      <p className="hr-workflows-card-audience">
                        {getWorkflowAudienceLabel(workflow.requestType, workflow)}
                      </p>
                    </div>
                    <div className="hr-workflows-card-flags">
                      <span
                        className={`hr-workflows-badge ${workflow.isActive === false ? "inactive" : "active"}`}
                      >
                        {workflow.isActive === false ? "متوقفة" : "نشطة"}
                      </span>
                      {workflow.openRequestCount ? (
                        <span className="hr-workflows-badge pending">
                          {workflow.openRequestCount} قيد المراجعة
                        </span>
                      ) : null}
                      {workflow.readOnly ? (
                        <span className="hr-workflows-badge">ثابتة</span>
                      ) : null}
                    </div>
                  </div>

                  <ol className="hr-workflows-path">
                    <li>
                      <span className="hr-workflows-path-label">
                        {getWorkflowFamilyKey(workflow.requestType) === "appeal"
                          ? "تقديم التظلم"
                          : "تقديم الطلب"}
                      </span>
                    </li>
                    {(workflow.steps || []).map((step) => {
                      const pending = stepPendingCount(workflow, step.stepOrder);
                      return (
                        <li key={step.id || step.stepOrder}>
                          <span className="hr-workflows-path-label">
                            {roleDisplayName(
                              step.approverRole?.slug,
                              step.approverRole?.name,
                            )}
                          </span>
                          {pending ? (
                            <span className="hr-workflows-path-count">
                              واقف عليها {pending}
                            </span>
                          ) : null}
                        </li>
                      );
                    })}
                  </ol>

                  {note ? <p className="hr-workflows-note">{note}</p> : null}

                  {editing ? (
                    <div className="hr-workflows-editor">
                      {workflow.openRequestCount ? (
                        <p className="hr-workflows-editor-warn">
                          {getWorkflowFamilyKey(workflow.requestType) === "appeal"
                            ? `فيه ${workflow.openRequestCount} تظلم مفتوح. التظلمات الواقفة على خطوة اتشالت هتنتقل للمستوى التالي.`
                            : `فيه ${workflow.openRequestCount} طلب مفتوح. الطلبات الحالية هتفضل على نفس رقم الخطوة بعد التعديل.`}
                        </p>
                      ) : null}
                      <label>
                        اسم السايكل
                        <input
                          value={draft.name}
                          onChange={(event) =>
                            updateDraft({ name: event.target.value })
                          }
                        />
                      </label>
                      <label>
                        وصف مختصر
                        <input
                          value={draft.description}
                          onChange={(event) =>
                            updateDraft({ description: event.target.value })
                          }
                        />
                      </label>
                      {(workflow.isCustom || workflow.id === "new") ? (
                        <>
                          {workflow.id === "new" ? (
                            <label>
                              نوع السايكل
                              <select
                                value={draft.family}
                                onChange={(event) =>
                                  updateDraft({ family: event.target.value })
                                }
                              >
                                {Object.entries(WORKFLOW_FAMILY_LABELS)
                                  .filter(([key]) => key !== "other")
                                  .map(([key, label]) => (
                                    <option key={key} value={key}>
                                      {label}
                                    </option>
                                  ))}
                              </select>
                            </label>
                          ) : null}
                          <div className="hr-workflows-employees">
                            <span className="hr-workflows-employees-title">
                              الموظفين اللي السايكل دي تخصّهم
                            </span>
                            <input
                              className="hr-workflows-search"
                              type="search"
                              value={employeeQuery}
                              onChange={(event) =>
                                setEmployeeQuery(event.target.value)
                              }
                              placeholder="ابحث باسم الموظف أو الكود"
                            />
                            <div className="hr-workflows-employee-list">
                              {employees
                                .filter((employee) => {
                                  const needle = employeeQuery.trim().toLowerCase();
                                  if (!needle) return true;
                                  return employeeLabel(employee)
                                    .toLowerCase()
                                    .includes(needle);
                                })
                                .slice(0, 80)
                                .map((employee) => (
                                  <label
                                    key={employee.id}
                                    className="hr-workflows-check"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={(draft.employeeIds || []).includes(
                                        employee.id,
                                      )}
                                      onChange={() => toggleEmployee(employee.id)}
                                    />
                                    {employeeLabel(employee)}
                                  </label>
                                ))}
                            </div>
                            <p className="hr-workflows-note">
                              تم اختيار {draft.employeeIds?.length || 0} موظف
                            </p>
                          </div>
                        </>
                      ) : null}
                      <label className="hr-workflows-check">
                        <input
                          type="checkbox"
                          checked={draft.isActive}
                          onChange={(event) =>
                            updateDraft({ isActive: event.target.checked })
                          }
                        />
                        السايكل شغالة
                      </label>

                      <div className="hr-workflows-steps">
                        {draft.steps.map((step, index) => (
                          <div key={`${step.stepOrder}-${index}`} className="hr-workflows-step-row">
                            <span className="hr-workflows-step-order">
                              {index + 1}
                            </span>
                            <select
                              value={step.approverRoleId}
                              onChange={(event) =>
                                updateStep(index, {
                                  approverRoleId: event.target.value,
                                })
                              }
                            >
                              <option value="">اختر الدور</option>
                              {roles.map((role) => (
                                <option key={role.id} value={role.id}>
                                  {roleDisplayName(role.slug, role.name)}
                                </option>
                              ))}
                            </select>
                            <label className="hr-workflows-check">
                              <input
                                type="checkbox"
                                checked={step.isRequired !== false}
                                onChange={(event) =>
                                  updateStep(index, {
                                    isRequired: event.target.checked,
                                  })
                                }
                              />
                              إلزامية
                            </label>
                            <div className="hr-workflows-step-actions">
                              <button
                                type="button"
                                onClick={() => moveStep(index, -1)}
                                disabled={index === 0}
                              >
                                أعلى
                              </button>
                              <button
                                type="button"
                                onClick={() => moveStep(index, 1)}
                                disabled={index === draft.steps.length - 1}
                              >
                                أسفل
                              </button>
                              <button
                                type="button"
                                className="danger"
                                onClick={() => removeStep(index)}
                                disabled={draft.steps.length === 1}
                              >
                                حذف
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="hr-workflows-editor-actions">
                        <button type="button" onClick={addStep}>
                          إضافة خطوة
                        </button>
                        <button
                          type="button"
                          className="primary"
                          onClick={() => handleSave(workflow)}
                          disabled={saving}
                        >
                          {saving ? "جاري الحفظ..." : "حفظ السايكل"}
                        </button>
                        <button type="button" onClick={cancelEdit} disabled={saving}>
                          إلغاء
                        </button>
                      </div>
                      {saveError ? (
                        <p className="hr-workflows-alert error">{saveError}</p>
                      ) : null}
                    </div>
                  ) : workflow.readOnly ? (
                    <p className="hr-workflows-note">
                      السايكل دي لسه مش متسجلة للتعديل. حدّث الصفحة بعد النشر.
                    </p>
                  ) : (
                    <button
                      type="button"
                      className="hr-workflows-edit-btn"
                      onClick={() => startEdit(workflow)}
                    >
                      تعديل السايكل
                    </button>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

export default HrWorkflows;
